/**
 * The colour arithmetic, kept apart from the palette that uses it.
 *
 * Its own file for two reasons. The console needs the same answers — it stamps
 * `--brand-primary` from the API at sign-in and has to correct the firm's accent against
 * whichever ground is showing — and two implementations of "is this readable?" would be two
 * opinions, disagreeing first on the firm that is nearly readable, which is the only firm
 * where it matters. And {@link ./theme} reaches for `expo-constants` through a guarded
 * `require` that a browser bundler should never have to look at; nothing here does.
 *
 * Enough arithmetic for the palette and no more. A colour library would be a dependency in
 * three surfaces for sixty lines.
 */

/** The two faces every surface has. */
export type Scheme = 'light' | 'dark';

/**
 * What somebody may choose, where they get to choose.
 *
 * `system` is the default everywhere it is offered, and is deliberately a third value
 * rather than the absence of one: "follow the handset" is a choice a person makes and can
 * come back to, and a preference that could only be expressed by clearing storage is a
 * preference nobody can express.
 */
export type Preference = Scheme | 'system';

/** The contrast a colour must reach against its ground to be read at ordinary sizes. */
export const AA_CONTRAST = 4.5;

/** The three channels of a `#rrggbb` string, or null if it is not one. */
function channels(hex: string): [number, number, number] | null {
  const value = hex.replace('#', '');

  if (value.length !== 6) return null;

  const parsed = [0, 2, 4].map((start) => Number.parseInt(value.slice(start, start + 2), 16));

  return parsed.some(Number.isNaN) ? null : (parsed as [number, number, number]);
}

function toHex(channelValues: number[]): string {
  return `#${channelValues
    .map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, '0'))
    .join('')}`;
}

/**
 * Lightens a hex colour towards white by a fraction.
 *
 * What makes a card read as a card on a dark ground: two fixed steps of this, rather than a
 * surface colour declared per brand that a firm could pick off its own background.
 */
export function lift(hex: string, amount: number): string {
  const rgb = channels(hex);

  return rgb === null ? hex : toHex(rgb.map((c) => c + (255 - c) * amount));
}

/**
 * Darkens a hex colour towards black by a fraction. The opposite of {@link lift}.
 *
 * The light palette's surfaces are sunk rather than lifted, because a lift applied to
 * near-white is invisible — and a card that does not read as a card is the whole of what a
 * surface token is for.
 */
export function sink(hex: string, amount: number): string {
  const rgb = channels(hex);

  return rgb === null ? hex : toHex(rgb.map((c) => c * (1 - amount)));
}

/**
 * A hex colour made see-through, as the `rgba()` React Native and a map's shapes both read.
 *
 * What lets a zone be filled in its heat's colour without hiding the streets under it. The
 * colour is the palette's, so it still follows the scheme; only the opacity is added here.
 */
export function translucent(hex: string, opacity: number): string {
  const [red, green, blue] = channels(hex) ?? [0, 0, 0];

  return `rgba(${red}, ${green}, ${blue}, ${Math.max(0, Math.min(1, opacity))})`;
}

/** Relative luminance, as WCAG defines it. */
export function luminance(hex: string): number {
  const rgb = channels(hex) ?? [0, 0, 0];

  const [r, g, b] = rgb.map((c) => {
    const channel = c / 255;

    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * How far apart two colours are, from 1 (identical) to 21 (black on white).
 *
 * The WCAG formula rather than a difference of brightness, because the two disagree exactly
 * where it matters: a mid-blue and a mid-green of the same brightness are 1.1 apart and
 * unreadable on each other, while brightness would call them fine.
 */
export function contrast(first: string, second: string): number {
  const one = luminance(first);
  const other = luminance(second);
  const [brighter, darker] = one > other ? [one, other] : [other, one];

  return (brighter + 0.05) / (darker + 0.05);
}

/** Whether a colour can be read at ordinary sizes on that ground. */
export function readable(colour: string, ground: string): boolean {
  return contrast(colour, ground) >= AA_CONTRAST;
}

/**
 * Nudges an accent until it can be read on the ground it is drawn on.
 *
 * A firm chooses one accent and we draw it on two grounds. A navy that looks right on paper
 * is invisible on `#0e1318`; a pale yellow is invisible on white. Neither firm is wrong and
 * neither should be told to pick a different colour, so the palette moves the colour instead
 * — away from the ground, in small steps, stopping the moment it clears AA. Small steps
 * because the point is to keep the firm's colour recognisable rather than to find the most
 * readable colour in its neighbourhood.
 *
 * **Idempotent**, which the tests assert: a colour that already clears is returned exactly
 * as it came in, so correcting a corrected accent is correcting it once. That matters
 * because the console re-runs this every time the scheme changes, on whatever value it last
 * stamped.
 */
export function readableOn(accent: string, ground: string): string {
  if (channels(accent) === null || readable(accent, ground)) return accent;

  // Away from the ground: lighter on a dark one, darker on a light one. Deciding by the
  // ground rather than by the accent is what stops a dark accent on a dark ground being
  // pushed further into it.
  const away = luminance(ground) < 0.5 ? lift : sink;

  for (let amount = 0.04; amount <= 1; amount += 0.04) {
    const moved = away(accent, amount);

    if (readable(moved, ground)) return moved;
  }

  // Unreachable for any real ground — full white clears 4.5 against anything dark enough
  // to have been sunk from, and full black against anything light. Returned rather than
  // thrown, because a palette that throws at module load takes the app down with it.
  return away(accent, 1);
}

/**
 * The ink for text sitting *inside* a colour rather than on the ground beside it.
 *
 * A filled button is the case: its label has to be read against the fill, and whether black
 * or white does that is a property of the fill rather than of the scheme. The console wrote
 * `#fff` on brand-filled buttons for a year, which is right for a dark ochre and wrong for
 * the firm whose accent is a pale yellow.
 */
export function inkOn(fill: string): string {
  return contrast('#0e1318', fill) >= contrast('#ffffff', fill) ? '#0e1318' : '#ffffff';
}
